import { db } from '../db/db.js';
import type { ShoppingItem } from '../db/schema.js';

export interface ShoppingView {
  planned: ShoppingItem[];
  ordered: ShoppingItem[];
  /** v0.4：已出貨在途中（團購 / 網購等）。優先依 expectedArrival 排序。 */
  inTransit: ShoppingItem[];
  recentReceived: ShoppingItem[];
  counts: {
    planned: number;
    ordered: number;
    inTransit: number;
    recentReceived: number;
    total: number;
  };
}

const RECENT_DAYS = 30;

export async function loadShoppingView(now: number = Date.now()): Promise<ShoppingView> {
  const all = await db.shoppingItems.toArray();
  const visible = all.filter((row) => row.status !== 'cancelled');

  const planned = visible
    .filter((r) => r.status === 'planned')
    .sort((a, b) => b.addedAt - a.addedAt);

  const ordered = visible
    .filter((r) => r.status === 'ordered')
    .sort((a, b) => {
      const ea = a.expectedArrival ?? a.orderedAt ?? a.addedAt;
      const eb = b.expectedArrival ?? b.orderedAt ?? b.addedAt;
      return ea - eb;
    });

  // 在途中：ETA 已知者依 ETA 升冪在前；ETA 未知者排最後（依 inTransitAt 新到舊）。
  const inTransit = visible
    .filter((r) => r.status === 'in_transit')
    .sort((a, b) => {
      const aHas = a.expectedArrival !== undefined;
      const bHas = b.expectedArrival !== undefined;
      if (aHas && bHas) return (a.expectedArrival ?? 0) - (b.expectedArrival ?? 0);
      if (aHas) return -1;
      if (bHas) return 1;
      return (b.inTransitAt ?? b.addedAt) - (a.inTransitAt ?? a.addedAt);
    });

  const cutoff = now - RECENT_DAYS * 86_400_000;
  const recentReceived = visible
    .filter((r) => r.status === 'received' && (r.receivedAt ?? 0) >= cutoff)
    .sort((a, b) => (b.receivedAt ?? 0) - (a.receivedAt ?? 0));

  return {
    planned,
    ordered,
    inTransit,
    recentReceived,
    counts: {
      planned: planned.length,
      ordered: ordered.length,
      inTransit: inTransit.length,
      recentReceived: recentReceived.length,
      total: planned.length + ordered.length + inTransit.length + recentReceived.length,
    },
  };
}
